import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, BadgeDollarSign, Link2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/DatePicker";
import api from "@/lib/api";

const FIELDS = ["fmv_409a", "fmv_date", "preferred_price", "expected_dilution_pct", "volatility_pct", "risk_free_pct", "expected_term_years", "exit_years"];

function SettingField({ k, label, hint, prefix, suffix, step = "any", testid, form, setForm }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="relative mt-1">
        {prefix && <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-xs text-muted-foreground">{prefix}</span>}
        <Input type="number" step={step} min={0} value={form[k] ?? ""} placeholder={hint} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className={`h-9 text-sm ${prefix ? "pl-6" : ""} ${suffix ? "pr-9" : ""}`} data-testid={testid} />
        {suffix && <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  );
}

export function EquitySettingsCard({ companyId }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [notify, setNotify] = useState(true);

  const load = () => api.get(`/companies/${companyId}/equity-settings`).then(({ data }) => {
    setData(data);
    setForm(Object.fromEntries(FIELDS.map((k) => [k, data.settings[k] ?? ""])));
  }).catch(() => setData({ error: true }));
  useEffect(() => { if (companyId) load(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    try {
      const { data: res } = await api.put(`/companies/${companyId}/equity-settings`, {
        fmv_409a: num(form.fmv_409a), fmv_date: form.fmv_date || null, preferred_price: num(form.preferred_price),
        expected_dilution_pct: num(form.expected_dilution_pct), volatility_pct: num(form.volatility_pct), risk_free_pct: num(form.risk_free_pct),
        expected_term_years: num(form.expected_term_years), exit_years: num(form.exit_years), notify_employees: notify,
      });
      if (res.changes?.length && res.notifying) toast.success(`Saved. ${res.changes.map((c) => c.label).join(", ")} changed - emailing ${res.notifying} employee${res.notifying === 1 ? "" : "s"}.`);
      else if (res.changes?.length) toast.success(`Saved. ${res.changes.map((c) => c.label).join(", ")} changed.`);
      else toast.success("Equity settings saved - every employee link updates instantly");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  if (!data) return null;
  const d = data.derived || {};
  const def = data.defaults || {};
  const fp = { form, setForm };
  return (
    <Card className="rounded-xl border bg-card p-5" data-testid="equity-settings-card">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground"><BadgeDollarSign className="h-4 w-4 text-primary" /> Employee equity settings</h2>
          <p className="text-xs text-muted-foreground">Powers the private equity page behind every option grant. Cap table supplies shares and preferences; you supply the 409A.</p>
        </div>
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${data.grants_linked >= data.equity_pages_limit ? "bg-amber-50 text-amber-800" : "bg-secondary text-muted-foreground"}`} data-testid="equity-links-count" title={`${data.plan_name} plan includes ${data.equity_pages_limit} employee equity pages`}>
          <Link2 className="h-3 w-3" /> {data.grants_linked}/{data.equity_pages_limit} equity pages used · {data.plan_name}
          {data.grants_linked >= data.equity_pages_limit && data.plan !== "scale" && data.plan !== "fund_manager" && <a href="/app/billing" className="ml-1 font-semibold underline" data-testid="equity-links-upgrade">Upgrade</a>}
        </span>
      </div>
      <form onSubmit={save} className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <SettingField {...fp} k="fmv_409a" label="409A FMV / share" prefix="$" hint="e.g. 0.85" testid="eq-fmv" />
        <div><Label className="text-xs">409A date</Label><DatePicker value={form.fmv_date || ""} onChange={(v) => setForm((f) => ({ ...f, fmv_date: v }))} className="mt-1 h-9" data-testid="eq-fmv-date" /></div>
        <SettingField {...fp} k="preferred_price" label="Preferred price / share" prefix="$" hint={d.preferred_price_derived ? `cap table: ${d.preferred_price_derived}` : "from last round"} testid="eq-pref-price" />
        <SettingField {...fp} k="expected_dilution_pct" label="Expected further dilution" suffix="%" hint={`default ${def.expected_dilution_pct}`} testid="eq-dilution" />
        <SettingField {...fp} k="volatility_pct" label="Volatility" suffix="%" hint={`default ${def.volatility_pct}`} testid="eq-volatility" />
        <SettingField {...fp} k="risk_free_pct" label="Risk-free rate" suffix="%" hint={`default ${def.risk_free_pct}`} testid="eq-risk-free" />
        <SettingField {...fp} k="expected_term_years" label="Expected term" suffix="yrs" hint={`default ${def.expected_term_years}`} testid="eq-term" />
        <SettingField {...fp} k="exit_years" label="Years to exit" suffix="yrs" hint={`default ${def.exit_years}`} testid="eq-exit-years" />
        <div className="col-span-2 flex flex-wrap items-end justify-between gap-3 sm:col-span-4">
          <div className="space-y-1.5">
            <p className="text-[11px] text-muted-foreground">Fully diluted {Math.round(d.fd_shares || 0).toLocaleString()} shares · {(d.preferred_classes || []).length} preferred class{(d.preferred_classes || []).length === 1 ? "" : "es"} · {Math.round(d.options_granted || 0).toLocaleString()} options granted</p>
            <label className="flex items-center gap-2 text-xs text-foreground">
              <Checkbox checked={notify} onCheckedChange={(v) => setNotify(!!v)} data-testid="eq-notify-employees" />
              Email employees with shared links when the 409A, preferred price or dilution changes
            </label>
          </div>
          <Button type="submit" size="sm" disabled={saving} className="gap-1.5" data-testid="eq-save">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save</Button>
        </div>
      </form>
    </Card>
  );
}
